<template>
  <div
    :id="uuid"
    class="p-6 bg-white rounded-xl"
    :class="[
      formData.blockShadow && 'shadow-lg',
      responsiveClasses.block,
      responsiveClasses.root,
    ]"
    :style="{
      'scroll-margin-top': pageEditorStore.scrollMarginTop,
      color: formData.textColor,
      'background-color': formData.bgColor,
      '--text-color': formData.textColor,
      ...useEditorFontFamily(formData.textFontFamily),
    }"
  >
    <h2
      v-if="formData.title && !(formData.hideOnEmbed && isInIframe)"
      class="text-2xl/[1] font-bold mb-2"
      :class="responsiveClasses.title"
      :style="{
        textAlign: formData.titleAlign,
        fontWeight: formData.titleWeight,
        fontStyle: formData.titleStyle,
        color: formData.titleColor,
        fontSize: formData.titleSizePx,
        ...useEditorFontFamily(formData.titleFontFamily),
      }"
    >
      {{ formData.title }}
    </h2>
    <div class="flex gap-6" :class="responsiveLayoutClass">
      <div
        class="flex flex-col gap-4"
        :class="hasSideContent ? 'flex-1' : 'w-full max-w-3xl mx-auto'"
      >
        <div
          class="marked"
          v-html="
            getMarkedTextWithBlankLinks(formData.text, formData.linkColor)
          "
        />
        <div
          v-if="formData.statCards?.length"
          class="grid gap-4 mt-auto pt-2"
          :class="gridColsClass"
        >
          <template v-for="(card, index) in formData.statCards" :key="index">
            <div
              class="rounded-xl px-4 py-5"
              :class="[
                responsiveClasses.statCard,
                formData.statCards.length % 2 === 1 &&
                  index === formData.statCards.length - 1 &&
                  responsiveClasses.lastStatCard,
              ]"
              :style="{
                color: formData.cardTextColor,
                backgroundColor: formData.cardBgColor,
              }"
            >
              <div
                class="text-4xl break-normal uppercase font-extralight mb-2"
                :class="responsiveClasses.statTitle"
              >
                {{ card.title }}
              </div>
              <div
                class="text-sm leading-4 font-light"
                :class="responsiveClasses.statSubtitle"
              >
                {{ card.subtitle }}
              </div>
            </div>
          </template>
        </div>
      </div>
      <div
        v-if="hasSideContent"
        class="shrink-0"
        :class="[formData.imageSize || 'w-1/3', responsiveClasses.sideContent]"
      >
        <img
          v-if="sideContentType === 'image'"
          :src="formData.image"
          class="w-full rounded-xl overflow-hidden"
        />
        <div
          v-else
          class="statistics-side-cards flex flex-col"
          :style="sideCardsContainerStyle"
        >
          <div
            v-for="(card, index) in formData.sideCards"
            :key="index"
            class="marked overflow-hidden"
            :class="[
              getSideCardPaddingClasses(card),
              card.borderRadius || 'rounded-xl',
            ]"
            :style="getSideCardStyle(card)"
            v-html="getMarkedTextWithBlankLinks(card.text, formData.linkColor)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { usePageEditor } from '~/stores/page-editor'
import { getGridColsClass } from '~/components/base/utils'
import {
  getCardPaddingClass,
  type CardPaddingSize,
} from '~/components/page-editor/card-padding'

const pageEditorStore = usePageEditor()

interface PropsInterface {
  uuid: string
  formData: any
  isInIframe?: boolean
}

const props = defineProps<PropsInterface>()

const { getMarkedTextWithBlankLinks } = useMarkedText()

const sideContentType = computed(
  () => props.formData.sideContentType || 'image'
)

const desktopBreakpoint = computed<'md' | 'lg'>(() =>
  !props.formData.imageSize || props.formData.imageSize === 'w-1/3'
    ? 'md'
    : 'lg'
)

const responsiveClasses = computed(() => {
  if (desktopBreakpoint.value === 'md') {
    return {
      root: 'statistics_desktop-md',
      block: 'md:p-8',
      title: 'md:text-3xl/[1.20] md:mb-6',
      statCard: 'md:px-3',
      lastStatCard: 'max-md:col-span-2',
      statTitle: 'md:text-[46px]',
      statSubtitle: 'md:text-base md:leading-5',
      sideContent: 'max-md:w-full',
    }
  }

  return {
    root: 'statistics_desktop-lg',
    block: 'lg:p-8',
    title: 'lg:text-3xl/[1.20] lg:mb-6',
    statCard: 'lg:px-3',
    lastStatCard: 'max-lg:col-span-2',
    statTitle: 'lg:text-[46px]',
    statSubtitle: 'lg:text-base lg:leading-5',
    sideContent: 'max-lg:w-full',
  }
})

const responsiveLayoutClass = computed(() => {
  if (desktopBreakpoint.value === 'md') {
    return props.formData.imagePosition === 'left'
      ? 'flex-col-reverse md:flex-row-reverse'
      : 'flex-col md:flex-row'
  }

  return props.formData.imagePosition === 'left'
    ? 'flex-col-reverse lg:flex-row-reverse'
    : 'flex-col lg:flex-row'
})

const hasSideContent = computed(() => {
  if (sideContentType.value === 'cards') {
    return Boolean(props.formData.sideCards?.length)
  }

  return Boolean(props.formData.image)
})

const toPixels = (value: unknown, fallback: number) => {
  const numberValue = Number(value)

  return `${
    Number.isFinite(numberValue) && numberValue >= 0 ? numberValue : fallback
  }px`
}

const sideCardsContainerStyle = computed(() => ({
  '--side-cards-padding-top-desktop': toPixels(
    props.formData.sideCardsContainerPaddingTopDesktop,
    0
  ),
  '--side-cards-padding-top-mobile': toPixels(
    props.formData.sideCardsContainerPaddingTopMobile,
    0
  ),
  '--side-cards-padding-bottom-desktop': toPixels(
    props.formData.sideCardsContainerPaddingBottomDesktop,
    0
  ),
  '--side-cards-padding-bottom-mobile': toPixels(
    props.formData.sideCardsContainerPaddingBottomMobile,
    0
  ),
  '--side-cards-padding-horizontal-desktop': toPixels(
    props.formData.sideCardsContainerPaddingHorizontalDesktop,
    0
  ),
  '--side-cards-padding-horizontal-mobile': toPixels(
    props.formData.sideCardsContainerPaddingHorizontalMobile,
    0
  ),
  '--side-cards-gap-desktop': toPixels(props.formData.sideCardsGapDesktop, 16),
  '--side-cards-gap-mobile': toPixels(props.formData.sideCardsGapMobile, 16),
}))

const getSideCardStyle = (card: any) => ({
  color: props.formData.textColor,
  backgroundColor: card.bgColor,
  '--text-color': props.formData.textColor,
})

const statisticsCardPaddingClasses = {
  horizontal: {
    base: 'px-4',
    md: 'md:px-3',
    lg: 'lg:px-3',
  },
  vertical: {
    base: 'py-5',
    md: 'md:py-5',
    lg: 'lg:py-5',
  },
}

const getSideCardPaddingClass = (
  size: CardPaddingSize | 'statistics-card' | undefined,
  axis: 'horizontal' | 'vertical',
  breakpoint: 'base' | 'md' | 'lg',
  fallback: CardPaddingSize
) => {
  if (size === 'statistics-card') {
    return statisticsCardPaddingClasses[axis][breakpoint]
  }

  return getCardPaddingClass(size, axis, breakpoint, fallback)
}

const getSideCardPaddingClasses = (card: any) => [
  getSideCardPaddingClass(
    card.paddingHorizontalMobile,
    'horizontal',
    'base',
    'md'
  ),
  getSideCardPaddingClass(
    card.paddingHorizontalDesktop,
    'horizontal',
    desktopBreakpoint.value,
    'md'
  ),
  getSideCardPaddingClass(card.paddingVerticalMobile, 'vertical', 'base', 'sm'),
  getSideCardPaddingClass(
    card.paddingVerticalDesktop,
    'vertical',
    desktopBreakpoint.value,
    'sm'
  ),
]

const gridColsClass = computed(() => {
  return getGridColsClass(
    props.formData.colsNumberOnDesktop,
    props.formData.colsNumberOnMobile,
    desktopBreakpoint.value
  )
})
</script>

<style scoped>
.statistics-side-cards {
  padding-top: var(--side-cards-padding-top-mobile);
  padding-right: var(--side-cards-padding-horizontal-mobile);
  padding-bottom: var(--side-cards-padding-bottom-mobile);
  padding-left: var(--side-cards-padding-horizontal-mobile);
  gap: var(--side-cards-gap-mobile);
}

@media (min-width: 768px) {
  .statistics_desktop-md .statistics-side-cards {
    padding-top: var(--side-cards-padding-top-desktop);
    padding-right: var(--side-cards-padding-horizontal-desktop);
    padding-bottom: var(--side-cards-padding-bottom-desktop);
    padding-left: var(--side-cards-padding-horizontal-desktop);
    gap: var(--side-cards-gap-desktop);
  }
}

@media (min-width: 1024px) {
  .statistics_desktop-lg .statistics-side-cards {
    padding-top: var(--side-cards-padding-top-desktop);
    padding-right: var(--side-cards-padding-horizontal-desktop);
    padding-bottom: var(--side-cards-padding-bottom-desktop);
    padding-left: var(--side-cards-padding-horizontal-desktop);
    gap: var(--side-cards-gap-desktop);
  }
}
</style>
